import LScreen from "@components/LScreen";
import LText from "@components/LText";
import LBox from "@components/LBox";
import LIcon from "@components/LIcon";
import { useAuthSheet } from "@components/modals/AuthModal/useAuthSheet";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import LButton from "@components/LButton";
import { useSubscribe } from "@hooks/useSubscribe";
import { IconName } from "@components/LIcon";
import LPressable from "@components/LPressable";
import { router } from "expo-router";
import { fonts, theme } from "@theme";
import { Platform } from "react-native";
import { useMe } from "@hooks/trpc/useMe";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import config from "@utils/config";
import { trackOnb } from "@components/Onboarding/analytics/trackOnboarding";
import { useOnboardingStore } from "@components/Onboarding/store/onboardingStore";
import { useLocalSearchParams } from "expo-router";
import { modal } from "@utils/modal/useModal";
import { auth } from "@utils/firebase";
import { captureException } from "@utils/captureException";
import { shouldCompletePaywallPurchase } from "@utils/paywallPurchase";

const MAXIMUM_SKIPS = 30;
type SubscriptionPlan = "monthly" | "yearly";

const getTrialPeriodLabel = ({
  periodNumberOfUnits,
  periodUnit,
}: {
  periodNumberOfUnits?: number;
  periodUnit?: string;
}) => {
  const unitCount = Number(periodNumberOfUnits);
  const normalizedUnit = periodUnit?.toLowerCase();

  if (!Number.isFinite(unitCount) || unitCount <= 0 || !normalizedUnit) {
    return null;
  }

  if (normalizedUnit.startsWith("day")) {
    return `${unitCount}-day`;
  }

  if (normalizedUnit.startsWith("week")) {
    return `${unitCount * 7}-day`;
  }

  if (normalizedUnit.startsWith("month")) {
    return `${unitCount * 30}-day`;
  }

  return null;
};

export const showPaywall = (infoVariant?: "skip" | "ad" | "playlist") => {
  modal.closeAllModals();
  router.push(infoVariant ? `/paywall?infoVariant=${infoVariant}` : "/paywall");
};

export default function PaywallScreen() {
  const safeAreaInsets = useSafeAreaInsets();
  const {
    subscribe,
    product,
    annualProduct,
    monthlyOffering,
    monthlyOfferingQuery,
    annualOffering,
    annualOfferingQuery,
  } = useSubscribe();
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan>("yearly");
  const {
    query: { data: user },
  } = useMe();
  const storeJourney = useOnboardingStore((s) => s.journey);
  const clearJourney = useOnboardingStore((s) => s.clearJourney);

  const params = useLocalSearchParams<{
    infoVariant?: "skip" | "ad" | "playlist";
    onboarding?: "true";
  }>();

  const infoVariant = params?.infoVariant;
  const isOnboarding = params.onboarding === "true";
  const journey = storeJourney ?? (isOnboarding ? "signup" : null);
  const reportedPaywallViewRef = useRef(false);
  const reportedOfferingLoadedRef = useRef(false);
  const reportedOfferingErrorRef = useRef<unknown>(null);
  const openAuthSheet = useAuthSheet();

  useEffect(() => {
    if (reportedPaywallViewRef.current) {
      return;
    }

    reportedPaywallViewRef.current = true;
    captureException("Paywall viewed", {
      channel: "breadcrumb",
      area: "paywall",
      operation: "view",
      tags: {
        revenuecat_surface: "paywall",
        paywall_phase: "view",
        ...(infoVariant ? { paywall_info_variant: infoVariant } : {}),
        ...(isOnboarding ? { paywall_onboarding: "true" } : {}),
      },
    });
  }, [infoVariant, isOnboarding]);

  useEffect(() => {
    if (isOnboarding && journey) {
      trackOnb(journey, "paywall", "view");
    }
  }, [isOnboarding, journey]);

  const isLoggedIn = !!user && !!auth.currentUser;
  const hasYearlyOption = !!annualOffering;
  const displayedAnnualProduct = annualProduct;
  const activePlan = hasYearlyOption ? selectedPlan : "monthly";
  const selectedOffering =
    activePlan === "yearly" ? annualOffering : monthlyOffering;
  const selectedOfferingQuery =
    activePlan === "yearly" ? annualOfferingQuery : monthlyOfferingQuery;
  const selectedProduct =
    activePlan === "yearly" ? displayedAnnualProduct : product;

  useEffect(() => {
    if (
      !isLoggedIn ||
      monthlyOffering ||
      !monthlyOfferingQuery.isError ||
      !monthlyOfferingQuery.error ||
      reportedOfferingErrorRef.current === monthlyOfferingQuery.error
    ) {
      return;
    }

    reportedOfferingErrorRef.current = monthlyOfferingQuery.error;
    captureException(monthlyOfferingQuery.error, {
      channel: "error",
      area: "paywall",
      fingerprint: ["paywall", "offering-load"],
      operation: "load-monthly-offering",
      tags: {
        revenuecat_surface: "paywall",
        paywall_phase: "offering",
      },
    });
  }, [
    isLoggedIn,
    monthlyOffering,
    monthlyOfferingQuery.error,
    monthlyOfferingQuery.isError,
  ]);

  useEffect(() => {
    if (!monthlyOffering || reportedOfferingLoadedRef.current) {
      return;
    }

    reportedOfferingLoadedRef.current = true;
    captureException("Monthly offering loaded", {
      channel: "breadcrumb",
      area: "paywall",
      operation: "load-monthly-offering-success",
      tags: {
        revenuecat_surface: "paywall",
        paywall_phase: "offering",
      },
    });
  }, [monthlyOffering]);

  const handleSubscribe = async () => {
    if (!isLoggedIn) {
      router.back();
      openAuthSheet();
      return;
    }

    if (activePlan === "yearly" && !annualOffering) {
      return;
    }

    if (!selectedOffering) {
      const error =
        selectedOfferingQuery.error ??
        new Error(`${activePlan} offering unavailable on paywall subscribe`);
      captureException(error, {
        channel: "error",
        area: "paywall",
        fingerprint: ["paywall", "offering-unavailable"],
        operation: "retry-load-subscription-offering",
        tags: {
          revenuecat_surface: "paywall",
          paywall_phase: "offering",
        },
      });
      await selectedOfferingQuery.refetch();
      return;
    }

    captureException("Paywall subscribe pressed", {
      channel: "breadcrumb",
      area: "paywall",
      operation: "subscribe-pressed",
      tags: {
        revenuecat_surface: "paywall",
        paywall_phase: "purchase",
      },
    });

    if (isOnboarding && journey) {
      trackOnb(journey, "paywall", "subscribe-pressed");
    }
    const result = await subscribe(selectedOffering);
    if (isOnboarding && journey) {
      if (result?.success) {
        trackOnb(journey, "paywall", "subscribed");
      } else if (result) {
        trackOnb(journey, "paywall", "subscribe-failed", {
          reason: result.userCancelled
            ? "cancelled"
            : result.pending
              ? "pending"
              : (result.errorCode ?? "error"),
        });
      }
    }

    // Only a verified Premium entitlement completes the purchase flow. Store
    // cancellation, deferred payment, and technical failures all leave the
    // paywall and onboarding journey intact so the user can retry immediately.
    if (!shouldCompletePaywallPurchase(result)) {
      return;
    }

    if (isOnboarding && journey) {
      clearJourney();
    }
    if (isOnboarding) {
      router.replace("/");
    } else if (result?.success && router.canGoBack()) {
      router.back();
    }
  };

  const handleDismiss = () => {
    if (isOnboarding && journey) {
      trackOnb(journey, "paywall", "dismissed");
      clearJourney();
    }
    if (isOnboarding) {
      router.replace("/");
    } else if (router.canGoBack()) {
      router.back();
    }
  };

  const buttonText = useMemo(() => {
    if (!isLoggedIn) {
      return "CREATE ACCOUNT";
    }
    if (config.PROJECT_ID === "lissendev") {
      return "NOT AVAILABLE IN DEV";
    }
    if (!selectedOffering) {
      if (selectedOfferingQuery.isError) {
        return "TRY AGAIN";
      }
      return selectedOfferingQuery.isFetched ? "NOT AVAILABLE" : "LOADING...";
    }
    return selectedProduct.hasFreeTrial ? "TRY IT FOR FREE" : "SUBSCRIBE";
  }, [
    isLoggedIn,
    selectedOffering,
    selectedOfferingQuery.isError,
    selectedOfferingQuery.isFetched,
    selectedProduct.hasFreeTrial,
  ]);

  const trialPeriodLabel = useMemo(
    () => getTrialPeriodLabel(selectedProduct.introPrice),
    [selectedProduct.introPrice],
  );

  const yearlySaving = useMemo(() => {
    if (
      product.price === undefined ||
      displayedAnnualProduct.price === undefined ||
      !displayedAnnualProduct.currencyCode
    ) {
      return null;
    }

    const saving = product.price * 12 - displayedAnnualProduct.price;
    if (saving <= 0) {
      return null;
    }

    const formattedSaving = new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(saving);
    const localizedYearlyPrice = displayedAnnualProduct.priceString;
    const firstDigitIndex = localizedYearlyPrice?.search(/[0-9]/) ?? -1;
    const lastDigitIndex = localizedYearlyPrice
      ? Math.max(
          ...Array.from(
            localizedYearlyPrice.matchAll(/[0-9]/g),
            (m) => m.index,
          ),
        )
      : -1;

    if (
      localizedYearlyPrice &&
      firstDigitIndex >= 0 &&
      lastDigitIndex >= firstDigitIndex
    ) {
      return `${localizedYearlyPrice.slice(0, firstDigitIndex)}${formattedSaving}${localizedYearlyPrice.slice(
        lastDigitIndex + 1,
      )}`;
    }

    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: displayedAnnualProduct.currencyCode,
      maximumFractionDigits: 2,
    }).format(saving);
  }, [
    displayedAnnualProduct.currencyCode,
    displayedAnnualProduct.price,
    displayedAnnualProduct.priceString,
    product.price,
  ]);

  return (
    <LScreen px="zero">
      {/* Background gradient */}
      <LBox
        backgroundColor={[
          "introBgGradient1",
          "introBgGradient2",
          "introBgGradient3",
          "screenBg",
        ]}
        height="45%"
        position="absolute"
        top={0}
        left={0}
        right={0}
      />

      {/* Close button */}
      <LBox
        style={{
          paddingTop:
            Platform.OS === "web" ? theme.spacing.l1 : safeAreaInsets.top,
        }}
        alignItems="flex-end"
        px="s3"
      >
        <LPressable sentryEventName={null} onPress={handleDismiss} fadeEnabled>
          <LBox
            backgroundColor="whiteTranslucent20"
            borderRadius="max"
            padding="s1"
            justifyContent="center"
            alignItems="center"
            width={32}
            height={32}
          >
            <LIcon name="cross" size={18} />
          </LBox>
        </LPressable>
      </LBox>

      <LBox
        flex={1}
        style={{
          paddingHorizontal: theme.spacing.s4,
          paddingBottom: safeAreaInsets.bottom + theme.spacing.s3,
        }}
      >
        {/* Top content */}
        <LBox
          alignItems="center"
          gap="s3"
          style={{ marginTop: -theme.spacing.s2 }}
        >
          {/* Logo */}
          <LBox mb="s2">
            <LIcon name="lissen_logo_full" sizeHeight={48} sizeWidth={160} />
          </LBox>

          {/* Headline */}
          <LBox alignItems="center">
            <LText
              fontSize={28}
              fontFamily={fonts.semiBold}
              lineHeight={34}
              textAlign="center"
              style={{
                textShadowColor: "rgba(0, 0, 0, 0.8)",
                textShadowOffset: { width: 0, height: 2 },
                textShadowRadius: 8,
              }}
            >
              Your artists. Your money.
            </LText>
            <LText
              fontSize={28}
              fontFamily={fonts.semiBold}
              lineHeight={34}
              textAlign="center"
              style={{
                color: "#F6B452",
                textShadowColor: "rgba(0, 0, 0, 0.8)",
                textShadowOffset: { width: 0, height: 2 },
                textShadowRadius: 8,
              }}
            >
              Your impact.
            </LText>
          </LBox>

          {/* Comparison cards */}
          <LBox
            flexDirection="row"
            gap="s3"
            mt="s2"
            mb="m1"
            justifyContent="space-evenly"
            width="100%"
          >
            <LBox
              borderRadius="l"
              borderWidth={1}
              paddingVertical="s3"
              alignItems="center"
              gap="s1"
              width={130}
              style={{
                backgroundColor: "#1E0D0D",
                borderColor: "rgba(255, 77, 61, 0.3)",
              }}
            >
              <LText
                fontSize={10}
                fontFamily={fonts.bold}
                letterSpacing={1}
                style={{ color: "#FF4D3D" }}
              >
                OTHER APPS
              </LText>
              <LText
                fontSize={36}
                fontFamily={fonts.extraBold}
                lineHeight={42}
                style={{
                  color: "#FF4D3D",
                  textShadowColor: "rgba(0, 0, 0, 0.8)",
                  textShadowOffset: { width: 0, height: 2 },
                  textShadowRadius: 8,
                }}
              >
                85%
              </LText>
              <LText
                fontSize={12}
                fontFamily={fonts.regular}
                color="whiteTranslucent60"
                textAlign="center"
                lineHeight={16}
              >
                Sent to artists{"\n"}you don&apos;t stream
              </LText>
            </LBox>
            <LBox
              borderRadius="l"
              borderWidth={1}
              paddingVertical="s3"
              alignItems="center"
              gap="s1"
              width={130}
              style={{
                backgroundColor: "#1B2D3E",
                borderColor: "rgba(61, 131, 247, 0.3)",
              }}
            >
              <LText
                fontSize={10}
                fontFamily={fonts.bold}
                letterSpacing={1}
                style={{ color: "#3D83F7" }}
              >
                LISSEN
              </LText>
              <LText
                fontSize={36}
                fontFamily={fonts.extraBold}
                lineHeight={42}
                style={{
                  color: "#3D83F7",
                  textShadowColor: "rgba(0, 0, 0, 0.8)",
                  textShadowOffset: { width: 0, height: 2 },
                  textShadowRadius: 8,
                }}
              >
                100%
              </LText>
              <LText
                fontSize={12}
                fontFamily={fonts.regular}
                color="whiteTranslucent60"
                textAlign="center"
                lineHeight={16}
              >
                Going to{"\n"}your favorite artists
              </LText>
            </LBox>
          </LBox>

          {/* Info variant sections */}
          {infoVariant === "skip" && <SkipLimitInfo />}
          {infoVariant === "ad" && <AdLimitInfo />}
          {infoVariant === "playlist" && <PlaylistLimitInfo />}

          {/* Feature points */}
          <LBox gap="s3" width="100%" px="s2">
            <FeaturePoint
              iconName="infinity"
              iconSize={10}
              title="Music, your way."
              description=" Zero ads, offline mode, no skips limit, unlimited playlists."
            />
            <FeaturePoint
              iconName="cash"
              title="Fan-centric royalties."
              description=" Your royalties only go to your artists."
            />
            <FeaturePoint
              iconName="access"
              title="Community-first."
              description=" Earn points, gems and unlock levels."
            />
          </LBox>
        </LBox>

        {/* Bottom: button + trial info pushed to bottom */}
        <LBox flex={1} justifyContent="flex-end">
          {isLoggedIn && hasYearlyOption && (
            <LBox flexDirection="row" gap="s3" mb="s2">
              <PlanOption
                label="Monthly"
                price={product.priceString}
                period="mo"
                selected={selectedPlan === "monthly"}
                onPress={() => setSelectedPlan("monthly")}
              />
              <PlanOption
                label="Yearly"
                price={displayedAnnualProduct.priceString}
                period="yr"
                saving={yearlySaving}
                selected={selectedPlan === "yearly"}
                onPress={() => setSelectedPlan("yearly")}
              />
            </LBox>
          )}
          {isLoggedIn && selectedOfferingQuery.isError && !selectedOffering && (
            <LText
              textAlign="center"
              fontFamily={fonts.regular}
              fontSize={14}
              lineHeight={21}
              color="whiteTranslucent60"
              mb="s3"
            >
              Could not load this subscription option. Check your connection and
              try again.
            </LText>
          )}
          <LBox width="100%">
            <LButton
              sentryEventName={null}
              label={buttonText}
              variant="roundedWhite"
              onPress={handleSubscribe}
              enabled={
                isLoggedIn
                  ? !!selectedOffering || selectedOfferingQuery.isError
                  : true
              }
            />
          </LBox>

          {isLoggedIn && selectedOffering && (
            <LBox alignItems="center" mt="s2">
              <LText
                textAlign="center"
                fontFamily={fonts.regular}
                fontSize={14}
                lineHeight={21}
                color="whiteTranslucent60"
              >
                {selectedProduct.hasFreeTrial && trialPeriodLabel
                  ? `${trialPeriodLabel} trial for free. Cancel anytime.`
                  : `${selectedProduct.priceString}/${
                      activePlan === "yearly" ? "year" : "month"
                    }. Cancel anytime.`}
              </LText>
              {selectedProduct.hasFreeTrial && (
                <LText
                  textAlign="center"
                  fontFamily={fonts.regular}
                  fontSize={14}
                  lineHeight={21}
                  color="whiteTranslucent60"
                >
                  Then{" "}
                  <LText
                    fontFamily={fonts.regular}
                    fontSize={14}
                    lineHeight={21}
                    color="white"
                  >
                    {selectedProduct.priceString || "—"}
                  </LText>
                  {selectedProduct.chargeDate
                    ? `/${activePlan === "yearly" ? "year" : "month"} from ${dayjs(
                        selectedProduct.chargeDate,
                      ).format("MMM D, YYYY")}`
                    : `/${activePlan === "yearly" ? "year" : "month"}`}
                </LText>
              )}
            </LBox>
          )}
          {!isLoggedIn && (
            <LBox alignItems="center" mt="s3">
              <LText
                textAlign="center"
                fontFamily={fonts.regular}
                fontSize={14}
                lineHeight={21}
                color="whiteTranslucent60"
              >
                Create an account to listen to more music
              </LText>
            </LBox>
          )}
        </LBox>
      </LBox>
    </LScreen>
  );
}

const PlanOption = ({
  label,
  price,
  period,
  saving,
  selected,
  onPress,
}: {
  label: string;
  price?: string;
  period: "mo" | "yr";
  saving?: string | null;
  selected: boolean;
  onPress: () => void;
}) => (
  <LPressable
    accessibilityRole="radio"
    accessibilityState={{ selected }}
    sentryEventName={null}
    onPress={onPress}
    fadeEnabled
    style={{ flex: 1 }}
  >
    <LBox
      borderRadius="l"
      borderWidth={selected ? 2 : 1}
      height={88}
      justifyContent="center"
      gap="s1"
      px="m1"
      style={{
        backgroundColor: selected ? "#20242B" : "#16191E",
        borderColor: selected ? "#FFFFFF" : "rgba(255, 255, 255, 0.08)",
      }}
    >
      <LText
        fontFamily={fonts.bold}
        fontSize={14}
        color={selected ? "white" : "whiteTranslucent60"}
      >
        {label}
      </LText>
      <LText
        fontFamily={fonts.bold}
        fontSize={24}
        lineHeight={30}
        color={selected ? "white" : "whiteTranslucent60"}
      >
        {price ?? "Unavailable"}
        {price && (
          <LText
            fontFamily={fonts.regular}
            fontSize={14}
            lineHeight={20}
            color="whiteTranslucent60"
          >
            /{period}
          </LText>
        )}
      </LText>
    </LBox>
    {!!saving && (
      <LBox
        position="absolute"
        right={8}
        top={-14}
        borderRadius="max"
        px="s2"
        style={{
          backgroundColor: theme.colors.saveBlue,
          paddingVertical: 2,
          zIndex: 1,
        }}
      >
        <LText fontFamily={fonts.bold} fontSize={11} color="white">
          Save {saving}
        </LText>
      </LBox>
    )}
  </LPressable>
);

export const PremiumFeaturePoints = () => {
  return (
    <>
      <Point title="Ad-free music listening" iconName="infinity" />
      <Point title="Unlimited Skips" iconName="skip" />
      <LBox flexDirection="row" alignItems="center" gap="s2">
        <Point title="Unlimited Exclusives" iconName="atom" />
        <LIcon colors={{ color: "#C5C5C5" }} name="info_filled" size={16} />
      </LBox>
      <Point title="Unlimited Playlists" iconName="playlists" />
      <Point title="Offline Mode" iconName="offline_mode" />
    </>
  );
};

const FeaturePoint = ({
  iconName,
  iconSize = 16,
  title,
  description,
}: {
  iconName: IconName;
  iconSize?: number;
  title: string;
  description: string;
}) => {
  return (
    <LBox flexDirection="row" alignItems="flex-start" gap="s3">
      <LBox
        backgroundColor="paywallPointIconBg"
        borderRadius="max"
        justifyContent="center"
        alignItems="center"
        width={36}
        height={36}
      >
        <LIcon name={iconName} size={iconSize} stroke="white" />
      </LBox>
      <LBox flex={1}>
        <LText fontSize={14} fontFamily={fonts.regular} lineHeight={21}>
          <LText fontSize={14} fontFamily={fonts.bold} lineHeight={21}>
            {title}
          </LText>
          {description}
        </LText>
      </LBox>
    </LBox>
  );
};

const Point = ({ title, iconName }: { title: string; iconName: IconName }) => {
  return (
    <LBox flexDirection="row" alignItems="center" gap="m1">
      <LBox
        backgroundColor="paywallPointIconBg"
        borderRadius="max"
        padding="s1"
        justifyContent="center"
        alignItems="center"
        width={32}
        height={32}
      >
        <LIcon name={iconName} size={16} />
      </LBox>
      <LText variant="bodyM2">{title}</LText>
    </LBox>
  );
};

const SkipLimitInfo = () => {
  return (
    <LBox
      borderWidth={0.5}
      borderColor="whiteBorder"
      borderRadius="l"
      bg="softNight"
      padding="m1"
      alignItems="center"
      width="100%"
    >
      <LText
        fontFamily={fonts.extraBold}
        fontSize={16}
        lineHeight={24}
        paddingBottom="s1"
      >
        Skip Limit Reached
      </LText>
      <LText
        textAlign="center"
        fontFamily={fonts.medium}
        fontSize={14}
        lineHeight={21}
        color="greyText"
      >
        You reached your limit of {MAXIMUM_SKIPS} skips per hour. {"\n"} Go
        Premium for unlimited skips!
      </LText>
    </LBox>
  );
};

const PlaylistLimitInfo = () => {
  return (
    <LBox
      borderWidth={0.5}
      borderColor="whiteBorder"
      borderRadius="l"
      bg="softNight"
      padding="m1"
      alignItems="center"
      width="100%"
    >
      <LText
        fontFamily={fonts.extraBold}
        fontSize={16}
        lineHeight={24}
        paddingBottom="s1"
      >
        Playlist Limit Reached
      </LText>
      <LText
        textAlign="center"
        fontFamily={fonts.medium}
        fontSize={14}
        lineHeight={21}
        color="greyText"
      >
        Go premium to create unlimited playlists!
      </LText>
    </LBox>
  );
};

const AdLimitInfo = () => {
  return (
    <LBox
      borderWidth={0.5}
      borderColor="whiteBorder"
      borderRadius="l"
      bg="softNight"
      padding="m1"
      alignItems="center"
      width="100%"
    >
      <LText
        fontFamily={fonts.extraBold}
        fontSize={16}
        lineHeight={24}
        paddingBottom="s1"
      >
        Advertisement
      </LText>
      <LText
        textAlign="center"
        fontFamily={fonts.medium}
        fontSize={14}
        lineHeight={21}
        color="greyText"
      >
        {`Freemium users support artists with ads\nGo Premium for unlimited Lissening!`}
      </LText>
    </LBox>
  );
};
